import React, { useEffect, useState } from "react";
import { getAllDocsSnap } from "../../../utils/firebase/Firebase.utils";
import ConcernsCard from "./ConcernsCard";
import PageModal from "../Components/PageModal";
import ConcernsPageDetails from "./ConcernsPageDetails";
import BentoTable from "../../../components/BentoTable";
import { slicedData } from "../../../utils/EleghoUtils";
import {
  FiAlertCircle,
  FiClock,
  FiCheckCircle,
  FiCalendar,
  FiPieChart,
  FiDownload,
} from "react-icons/fi";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import AddConcernForm from "./AddConcernForm";
import * as XLSX from "xlsx";

function JobOrders() {
  const [activeFilter, setActiveFilter] = React.useState("Pending");
  const [concerns, setConcerns] = React.useState([]);
  const [concernTypeFilter, setConcernTypeFilter] = React.useState("All");

  const [filteredJobOrders, setFilteredJobOrders] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(true);
  const [viewMode, setViewMode] = useState(false);
  const [selectedConcern, setSelectedConcern] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [numberOfItemPerPage, setNumberOfItemPerPage] = useState(5);
  const [addMode, setAddMode] = useState(false);

  const concernList = [
    "Electricity",
    "Water Supply",
    "Water Leaks",
    "Internet",
    "Violation",
    "Neighbors",
    "Defective Water Meter",
    "Others",
  ];

  // Enhanced color palette with more vibrant and harmonious colors
  const COLORS = [
    "#4F46E5", // Indigo
    "#0EA5E9", // Sky Blue
    "#10B981", // Emerald
    "#F59E0B", // Amber
    "#EF4444", // Red
    "#8B5CF6", // Purple
    "#EC4899", // Pink
    "#14B8A6", // Teal
    "#6B7280", // Gray
  ];

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-100">
          <p className="font-semibold text-gray-800">{payload[0].name}</p>
          <p className="text-gray-600">
            Count: <span className="font-medium">{payload[0].value}</span>
          </p>
          <p className="text-gray-600">
            Percentage:{" "}
            <span className="font-medium">
              {((payload[0].value / concerns.length) * 100).toFixed(1)}%
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Calculate concern type distribution
  const getConcernTypeDistribution = () => {
    const distribution = {};
    concernList.forEach((type) => {
      distribution[type] = 0;
    });

    concerns.forEach((concern) => {
      if (distribution[concern.concern] !== undefined) {
        distribution[concern.concern]++;
      } else {
        distribution["Others"]++;
      }
    });

    // Convert to array and sort alphabetically, keeping Others last
    return Object.entries(distribution)
      .map(([name, value]) => ({
        name,
        value,
      }))
      .sort((a, b) => {
        if (a.name === "Others") return 1;
        if (b.name === "Others") return -1;
        return a.name.localeCompare(b.name);
      });
  };

  useEffect(() => {
    const fetchConcerns = async () => {
      await getAllDocsSnap("concerns", (data) => {
        // arrange data by createdAt
        data.sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setConcerns(data);
        setFilteredJobOrders(data);
        setIsLoading(false);
      });
    };
    fetchConcerns();
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const statusLinks = ["Pending", "Scheduled", "Resolved"];

  const tableHeaders = [
    "Unit",
    "Description",
    "Photo",
    "Status",
    "Date",
    "Assigned To",
    "Date Assigned",
    "Priority",
    "Requested By",
    "Actions",
  ];

  const filterLinks = [
    "All",
    "Pending",
    "Processing",
    "Dispatched",
    "Resolved",
  ];

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const activeConcerns = concerns.filter((concern) => {
    if (activeFilter === "All") {
      return concern;
    }
    return concern.status.toLowerCase() === activeFilter.toLowerCase();
  });

  const filteredConcerns = activeConcerns.filter((concern) => {
    const matchesSearch =
      concern.identifier.toLowerCase().includes(searchTerm.toLowerCase()) ||
      concern.concern.toLowerCase().includes(searchTerm.toLowerCase());

    if (concernTypeFilter === "All") {
      return matchesSearch;
    }
    return matchesSearch && concern.concern === concernTypeFilter;
  });

  const handleViewMode = (selectedCard) => {
    setViewMode(!viewMode);
    setSelectedConcern(selectedCard);
  };

  const currentItems = slicedData(
    filteredConcerns,
    numberOfItemPerPage,
    currentPage
  );

  // Calculate stats for the summary cards
  const stats = {
    totalConcerns: concerns.length,
    pendingConcerns: concerns.filter((c) => c.status === "Pending").length,
    resolvedConcerns: concerns.filter((c) => c.status === "Resolved").length,
    scheduledConcerns: concerns.filter(
      (c) => c.status === "Scheduled" || c.status === "Dispatched"
    ).length,
  };

  const slidedData = filteredConcerns.slice(
    (currentPage - 1) * numberOfItemPerPage,
    currentPage * numberOfItemPerPage
  );

  const downloadExcel = () => {
    // Prepare the data for Excel
    const excelData = filteredConcerns.map((concern) => ({
      Unit: concern.identifier,
      "Concern Type": concern.concern,
      Description: concern.description,
      Status: concern.status,
      Date: new Date(concern.createdAt).toLocaleDateString(),
      "Assigned To": concern.assignedTo || "Not Assigned",
      "Date Assigned": concern.dateAssigned
        ? new Date(concern.dateAssigned).toLocaleDateString()
        : "Not Assigned",
      Priority: concern.priority || "Not Set",
      "Requested By": concern.requestedBy || "Not Specified",
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    const wscols = [
      { wch: 15 }, // Unit
      { wch: 20 }, // Concern Type
      { wch: 40 }, // Description
      { wch: 15 }, // Status
      { wch: 15 }, // Date
      { wch: 20 }, // Assigned To
      { wch: 15 }, // Date Assigned
      { wch: 15 }, // Priority
      { wch: 20 }, // Requested By
    ];
    ws["!cols"] = wscols;

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Concerns");

    // Generate Excel file
    XLSX.writeFile(
      wb,
      `concerns_report_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  return (
    <div className="text-xs">
      <div>
        <div className="mt-4 font-bold text-blue-900 uppercase text-left text-sm">
          Residents' Concerns
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-6">
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-50 rounded-lg">
                <FiAlertCircle className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Total Concerns</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {stats.totalConcerns}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-eleghoGreen/10 rounded-lg">
                <FiCheckCircle className="w-5 h-5 text-eleghoGreen" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Resolved</div>
                <div className="text-2xl font-semibold text-eleghoGreen">
                  {stats.resolvedConcerns}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-lg">
                <FiClock className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <div className="text-sm text-gray-600">Pending</div>
                <div className="text-2xl font-semibold text-amber-500">
                  {stats.pendingConcerns}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-eleghoBlue/10 rounded-lg">
                <FiCalendar className="w-5 h-5 text-eleghoBlue" />
              </div>
              <div>
                <div className="text-sm text-gray-600">
                  Scheduled/Dispatched
                </div>
                <div className="text-2xl font-semibold text-eleghoBlue">
                  {stats.scheduledConcerns}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Pie Chart */}
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow mb-6">
          <div className="flex items-center gap-2 mb-6">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <FiPieChart className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">
              Concern Types Distribution
            </h3>
          </div>
          <div className="h-[400px] flex justify-center items-center">
            <div className="w-[60%] h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getConcernTypeDistribution()}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={140}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) =>
                      percent > 0.05
                        ? `${name} (${(percent * 100).toFixed(0)}%)`
                        : ""
                    }
                    labelLine={false}
                  >
                    {getConcernTypeDistribution().map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        className="hover:opacity-80 transition-opacity"
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-[35%] h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getConcernTypeDistribution()}
                    dataKey="value"
                    cx="0"
                    cy="0"
                    innerRadius={0}
                    outerRadius={0}
                  >
                    {getConcernTypeDistribution().map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Legend
                    verticalAlign="middle"
                    align="left"
                    layout="vertical"
                    formatter={(value) => (
                      <span className="text-sm text-gray-600">{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-normal gap-4 mt-2">
          {filterLinks.map((link, index) => (
            <div
              key={index}
              className={`cursor-pointer text-sm rounded-full border-2 border-gray-300 px-2 py-1 min-w-20 text-center ${
                activeFilter === link ? "active-pill" : "inactive-pill "
              }
                `}
              onClick={() => setActiveFilter(link)}
            >
              {link}
            </div>
          ))}
        </div>

        <div className="mt-4">
          <button className="blue-button" onClick={() => setAddMode(!addMode)}>
            Add New
          </button>
        </div>

        <div className="flex items-center justify-between p-2 rounded-lg border-2 border-gray-300 mt-4">
          <div className="flex items-center gap-4">
            <input
              type="search"
              placeholder="Search by Address or Concern"
              className="border-2 border-gray-300 px-4 py-2 rounded-lg w-60"
              onChange={handleSearch}
            />
            <select
              value={concernTypeFilter}
              onChange={(e) => setConcernTypeFilter(e.target.value)}
              className="border-2 border-gray-300 px-4 py-2 rounded-lg w-60"
            >
              <option value="All">All Concern Types</option>
              {concernList.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={downloadExcel}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiDownload className="w-4 h-4" />
            Export to Excel
          </button>
        </div>

        <BentoTable
          label="Residents' Concerns"
          data={filteredConcerns}
          numberOfItemPerPage={numberOfItemPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        >
          <div className="divide-y divide-gray-100">
            <div className="grid grid-cols-5 gap-2 p-3 bg-gray-50 text-xs font-medium text-gray-500">
              <div>Unit</div>
              <div>Concern</div>
              <div>Description</div>
              <div>Filed on</div>
              <div>Status</div>
            </div>
            {slidedData.map((concern, index) => (
              <div
                key={index}
                onClick={() => handleViewMode(concern)}
                className="hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <ConcernsCard concern={concern} />
              </div>
            ))}
          </div>
        </BentoTable>
      </div>

      {viewMode && (
        <PageModal>
          <ConcernsPageDetails
            concern={selectedConcern}
            setViewMode={setViewMode}
          />
        </PageModal>
      )}

      {addMode && (
        <PageModal>
          <AddConcernForm setAddMode={setAddMode} />
        </PageModal>
      )}
    </div>
  );
}

export default JobOrders;
